/**
 * Google Analytics 4 Integration
 * Set REACT_APP_GA_MEASUREMENT_ID in .env files to enable
 */

let gaInitialized = false;

/**
 * Initialize Google Analytics
 */
export function initGA() {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (!measurementId) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[GA] No measurement ID provided - analytics disabled');
    }
    return;
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false, // We'll send manually
  });

  gaInitialized = true;
}

/**
 * Track page view
 */
export function trackPageView(path, title) {
  if (!gaInitialized || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
}

/**
 * Track custom event
 */
export function trackEvent(eventName, params = {}) {
  if (!gaInitialized || !window.gtag) return;

  window.gtag('event', eventName, params);
}

/**
 * Track search performed
 */
export function trackSearch(searchParams) {
  trackEvent('search', {
    search_term: `${searchParams.from} to ${searchParams.to}`,
    origin: searchParams.from,
    destination: searchParams.to,
    departure_date: searchParams.departureDate,
    return_date: searchParams.returnDate,
    passengers: searchParams.adults + searchParams.children + searchParams.infants,
  });
}

/**
 * Track flight selection
 */
export function trackFlightSelect(flight) {
  trackEvent('select_item', {
    item_id: flight.id,
    item_name: `${flight.origin} to ${flight.destination}`,
    item_category: 'flight',
    price: flight.totalPrice,
    currency: flight.currency,
  });
}

/**
 * Track booking started
 */
export function trackBeginCheckout(bookingData) {
  trackEvent('begin_checkout', {
    value: bookingData.totalPrice,
    currency: bookingData.currency,
    items: [{
      item_id: bookingData.offerId,
      item_name: `${bookingData.origin} to ${bookingData.destination}`,
      item_category: 'flight',
      price: bookingData.totalPrice,
      quantity: 1,
    }],
  });
}

/**
 * Track successful booking
 */
export function trackPurchase(booking) {
  trackEvent('purchase', {
    transaction_id: booking.id,
    value: booking.totalPrice,
    currency: booking.currency,
    items: [{
      item_id: booking.offerId,
      item_name: `${booking.origin} to ${booking.destination}`,
      item_category: 'flight',
      price: booking.totalPrice,
      quantity: 1,
    }],
  });
}

/**
 * Track user signup
 */
export function trackSignUp(method = 'email') {
  trackEvent('sign_up', { method });
}

/**
 * Track user login
 */
export function trackLogin(method = 'email') {
  trackEvent('login', { method });
}

/**
 * Set user properties
 */
export function setUserProperties(userId) {
  if (!gaInitialized || !window.gtag) return;

  window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
    user_id: userId,
  });
}

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackSearch,
  trackFlightSelect,
  trackBeginCheckout,
  trackPurchase,
  trackSignUp,
  trackLogin,
  setUserProperties,
};
